/**
 * Qual layout usar agora — preferência gravada no aparelho + tamanho da tela.
 *
 * Reage a girar o tablet e a redimensionar a janela, e a troca feita num lugar
 * (menu "Mais", rodapé do menu lateral) vale na hora para a página inteira.
 */
import { useCallback, useSyncExternalStore } from "react";
import { useAuth } from "../../lib/auth";
import {
  hasMobileLayout, LAYOUT_STORAGE_KEY, parsePreference, preferenceFor, resolveLayout,
  type Device, type Layout, type LayoutPreference,
} from "./mobile-layout";

const EVENTO = "siag:layout-manutencao";

function lerPreferencia(): LayoutPreference {
  try { return parsePreference(window.localStorage.getItem(LAYOUT_STORAGE_KEY)); } catch { return "auto"; }
}

function gravarPreferencia(p: LayoutPreference) {
  try {
    if (p === "auto") window.localStorage.removeItem(LAYOUT_STORAGE_KEY);
    else window.localStorage.setItem(LAYOUT_STORAGE_KEY, p);
  } catch { /* modo privado: a troca vale só nesta sessão da página */ }
  window.dispatchEvent(new Event(EVENTO));
}

function lerAparelho(): Device {
  return {
    width: window.innerWidth,
    coarsePointer: window.matchMedia?.("(pointer: coarse)").matches ?? false,
  };
}

function assinar(avisar: () => void) {
  window.addEventListener("resize", avisar);
  window.addEventListener("orientationchange", avisar);
  window.addEventListener("storage", avisar);
  window.addEventListener(EVENTO, avisar);
  return () => {
    window.removeEventListener("resize", avisar);
    window.removeEventListener("orientationchange", avisar);
    window.removeEventListener("storage", avisar);
    window.removeEventListener(EVENTO, avisar);
  };
}

// Snapshot como string: useSyncExternalStore compara por igualdade.
const snapshot = () => resolveLayout(lerPreferencia(), lerAparelho());

export function useMobileLayout(): {
  /** o usuário tem os dois layouts */
  available: boolean;
  layout: Layout;
  switchTo: (alvo: Layout) => void;
} {
  const { user } = useAuth();
  const available = hasMobileLayout(user);
  const atual = useSyncExternalStore(assinar, snapshot, () => "desktop" as Layout);
  const switchTo = useCallback((alvo: Layout) => {
    gravarPreferencia(preferenceFor(alvo, lerAparelho()));
    window.scrollTo(0, 0);
  }, []);
  return { available, layout: available ? atual : "desktop", switchTo };
}
